import React, { useState, useEffect, useMemo, useCallback } from "react";
import Layout from "../../Component/Layout";
import Select from "react-select";
import axios from "axios";
import { MASTER_API } from "../../apiConfig";

// Reuses the SAME module-process-config source Help Desk uses, so Module /
// Process / Sub Process stay in sync with that single config table.
const CONFIG_ENDPOINTS = {
  moduleProcessConfig: `${MASTER_API}/configuration/dynamic/module-process-config`
};

const USER_MANUAL_API = {
  list: `${MASTER_API}/user-manual/list`,
  upload: `${MASTER_API}/user-manual/upload`,
  delete: (id) => `${MASTER_API}/user-manual/delete/${id}`
};

const styles = {
  page: { background: "#f8fafc", minHeight: "100vh", padding: "20px" },
  pageTitleBar: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" },
  pageTitle: { margin: 0, fontSize: "20px", fontWeight: 700, color: "#1e293b" },
  pageSubtitle: { margin: "2px 0 0 0", fontSize: "12px", color: "#64748b" },
  createBtn: {
    background: "#021843", color: "#fff", border: "1px solid #021843", padding: "8px 18px",
    borderRadius: "6px", fontSize: "13px", fontWeight: 600, cursor: "pointer"
  },
  filterCard: { background: "#ffffff", borderRadius: 8, padding: 15, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", marginBottom: 15 },
  compactLabel: { fontSize: "12px", fontWeight: 600, marginBottom: "2px", color: "#334155", display: "block" },
  tableContainer: { background: "#ffffff", borderRadius: 8, padding: 10, boxShadow: "0 1px 4px rgba(0,0,0,0.08)" },
  erpTable: { width: "100%", borderCollapse: "collapse" },
  erpTh: { background: "#f8fafc", fontSize: 12, fontWeight: 600, padding: "10px", borderBottom: "1px solid #e5e7eb", color: "#1e293b", whiteSpace: "nowrap" },
  erpTd: { fontSize: 13, padding: "10px", borderBottom: "1px solid #f1f5f9", color: "#334155" },
  modalBackdrop: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 9999 },
  modal: { width: "95vw", maxWidth: "560px", maxHeight: "90vh", overflowY: "auto", background: "#fff", borderRadius: "10px" },
  alertBox: (type) => ({
    display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px",
    borderRadius: "6px", fontSize: "13px", fontWeight: 500, marginBottom: "14px",
    background: type === "success" ? "#dcfce7" : "#fdecea",
    color: type === "success" ? "#166534" : "#b91c1c",
    border: type === "success" ? "1px solid #86efac" : "1px solid #f5c2c7"
  })
};

const customSelectStyles = {
  control: (base) => ({ ...base, minHeight: 32, height: 32, fontSize: "13px", borderColor: "#dee2e6", boxShadow: "none" }),
  valueContainer: (base) => ({ ...base, padding: "0 8px", height: "32px", display: "flex", alignItems: "center" }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  menu: (base) => ({ ...base, fontSize: "13px", zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? "#1e293b" : state.isFocused ? "#f1f5f9" : "white",
    color: state.isSelected ? "white" : "#334155",
    cursor: "pointer",
    fontSize: "13px"
  })
};

const uniqueOptions = (values) => {
  const seen = new Set();
  const out = [];
  (values || []).forEach((v) => {
    const val = String(v || "").trim();
    if (!val || seen.has(val)) return;
    seen.add(val);
    out.push({ value: val, label: val });
  });
  return out;
};

const getCurrentUser = () => {
  const userObj = localStorage.getItem("user");
  if (userObj) {
    try {
      const parsedUser = JSON.parse(userObj);
      const fullName = `${parsedUser.firstName || ""} ${parsedUser.lastName || ""}`.trim();
      if (fullName) return fullName;
      if (parsedUser.email) return parsedUser.email;
    } catch (e) { console.error(e); }
  }
  const email = localStorage.getItem("email");
  if (email && email !== "undefined" && email !== "null") return email;
  return "User";
};

const getFileIcon = (name = "") => {
  const ext = (name.split(".").pop() || "").toLowerCase();
  if (ext === "pdf") return "📄";
  if (["doc", "docx"].includes(ext)) return "📝";
  if (["xls", "xlsx", "csv"].includes(ext)) return "📊";
  if (["png", "jpg", "jpeg"].includes(ext)) return "🖼️";
  return "📎";
};

const createEmptyUpload = () => ({
  moduleName: "",
  process: "",
  subProcess: "",
  file: null
});

function UserManual() {
  const [manuals, setManuals] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [uploadForm, setUploadForm] = useState(createEmptyUpload());
  const [uploading, setUploading] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [alert, setAlert] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [configOptions, setConfigOptions] = useState({ moduleProcessConfig: [] });

  const [filters, setFilters] = useState({ module: null, process: null, subProcess: null });

  useEffect(() => {
    if (!alert) return;
    const t = setTimeout(() => setAlert(null), 5000);
    return () => clearTimeout(t);
  }, [alert]);

  const fetchConfigOptions = useCallback(async () => {
    try {
      const res = await axios.get(CONFIG_ENDPOINTS.moduleProcessConfig).catch(() => ({ data: [] }));
      setConfigOptions({ moduleProcessConfig: res.data || [] });
    } catch (err) {
      console.error("Failed to load module/process config", err);
    }
  }, []);

  // Wired to the real backend now — GET /user-manual/list returns
  // { success, data: [...] } where each row already has module_name /
  // process_name / sub_process_name / file_name / uploaded_by /
  // uploaded_at / drive_link.
  const fetchManuals = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await axios.get(USER_MANUAL_API.list);
      setManuals(res.data?.data || []);
    } catch (err) {
      console.error("Failed to load user manuals", err);
      setAlert({ type: "error", message: "Failed to load user manuals." });
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    fetchConfigOptions();
    fetchManuals();
  }, [fetchConfigOptions, fetchManuals]);

  const mpc = configOptions.moduleProcessConfig;

  const moduleOptions = useMemo(() => uniqueOptions(mpc.map((r) => r.module_name)), [mpc]);

  const getProcessOptions = (moduleName) =>
    uniqueOptions(mpc.filter((r) => !moduleName || (r.module_name || "").trim() === moduleName).map((r) => r.process_name));

  const getSubProcessOptions = (moduleName, processName) =>
    uniqueOptions(
      mpc
        .filter((r) => (!moduleName || (r.module_name || "").trim() === moduleName) &&
                       (!processName || (r.process_name || "").trim() === processName))
        .map((r) => r.sub_process_name)
    );

  const handleUploadFieldChange = (field, value) => {
    setUploadForm((prev) => {
      if (field === "moduleName") return { ...prev, moduleName: value, process: "", subProcess: "" };
      if (field === "process") return { ...prev, process: value, subProcess: "" };
      return { ...prev, [field]: value };
    });
  };

  // Real upload — multipart to POST /user-manual/upload. The backend does
  // the CableNXT/User Manuals/<Module>/<Process>/<SubProcess> Drive folder
  // walk and comes back with the saved row (drive_link included).
    const handleUpload = async () => {
    if (!uploadForm.moduleName) {
      setAlert({ type: "error", message: "Module is required." });
      return;
    }
    if (!uploadForm.process) {
      setAlert({ type: "error", message: "Process is required. Uploading against a Module alone is not allowed." });
      return;
    }
    if (!uploadForm.file) {
      setAlert({ type: "error", message: "Please choose a file to upload." });
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadForm.file);
      formData.append("moduleName", uploadForm.moduleName);
      formData.append("process", uploadForm.process);
      formData.append("subProcess", uploadForm.subProcess);
      formData.append("uploadedBy", getCurrentUser());

      const res = await axios.post(USER_MANUAL_API.upload, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (res.data?.success) {
        setManuals((prev) => [res.data.data, ...prev]);
        setAlert({ type: "success", message: "User manual uploaded successfully." });
        setShowModal(false);
        setUploadForm(createEmptyUpload());
      } else {
        setAlert({ type: "error", message: res.data?.message || "Failed to upload the user manual." });
      }
    } catch (err) {
      console.error(err);
      setAlert({ type: "error", message: err.response?.data?.message || "Failed to upload the user manual." });
    } finally {
      setUploading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget.id;
    setDeleteTarget(null);
    try {
      await axios.delete(USER_MANUAL_API.delete(id));
      setManuals((prev) => prev.filter((m) => m.id !== id));
      setAlert({ type: "success", message: "User manual deleted." });
    } catch (err) {
      console.error(err);
      setAlert({ type: "error", message: "Failed to delete the user manual." });
    }
  };

  const filteredManuals = useMemo(() => {
    return manuals.filter((m) => {
      if (filters.module && m.module_name !== filters.module.value) return false;
      if (filters.process && m.process_name !== filters.process.value) return false;
      if (filters.subProcess && m.sub_process_name !== filters.subProcess.value) return false;
      return true;
    });
  }, [manuals, filters]);

  const formatDate = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "-";
    return d.toLocaleDateString("en-GB");
  };

  return (
    <Layout>
      <div style={styles.page}>
        {alert && !showModal && (
          <div style={styles.alertBox(alert.type)}>
            <span>{alert.message}</span>
            <button onClick={() => setAlert(null)} style={{ background: "none", border: "none", fontWeight: 700, cursor: "pointer", color: "inherit", fontSize: "14px" }}>&times;</button>
          </div>
        )}

        <div style={styles.pageTitleBar}>
          <div>
            <h2 style={styles.pageTitle}>Update User Manual</h2>
            <p style={styles.pageSubtitle}>Upload and manage user manuals for each module, process and sub process</p>
          </div>

          <button
            style={styles.createBtn}
            onClick={() => { setUploadForm(createEmptyUpload()); setAlert(null); setShowModal(true); }}
          >
            + Upload User Manual
          </button>
        </div>

        {/* ===================== FILTERS ===================== */}
        <div style={styles.filterCard}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            <div style={{ minWidth: 200, flex: "1 1 200px" }}>
              <label style={styles.compactLabel}>Module</label>
              <Select
                styles={customSelectStyles}
                options={moduleOptions}
                value={filters.module}
                placeholder="Module"
                isClearable
                onChange={(v) => setFilters((prev) => ({ ...prev, module: v, process: null, subProcess: null }))}
              />
            </div>
            <div style={{ minWidth: 200, flex: "1 1 200px" }}>
              <label style={styles.compactLabel}>Process</label>
              <Select
                styles={customSelectStyles}
                options={getProcessOptions(filters.module?.value)}
                value={filters.process}
                placeholder="Process"
                isClearable
                onChange={(v) => setFilters((prev) => ({ ...prev, process: v, subProcess: null }))}
              />
            </div>
            <div style={{ minWidth: 200, flex: "1 1 200px" }}>
              <label style={styles.compactLabel}>Sub Process</label>
              <Select
                styles={customSelectStyles}
                options={getSubProcessOptions(filters.module?.value, filters.process?.value)}
                value={filters.subProcess}
                placeholder="Sub Process"
                isClearable
                onChange={(v) => setFilters((prev) => ({ ...prev, subProcess: v }))}
              />
            </div>
          </div>
        </div>

        {/* ===================== TABLE ===================== */}
        <div style={styles.tableContainer}>
          {loadingList && <div style={{ padding: "10px 4px", color: "#64748b", fontSize: 13 }}>Loading...</div>}
          <table className="table" style={styles.erpTable}>
            <thead>
              <tr>
                <th style={styles.erpTh}>Module</th>
                <th style={styles.erpTh}>Process</th>
                <th style={styles.erpTh}>Sub Process</th>
                <th style={styles.erpTh}>File Name</th>
                <th style={styles.erpTh}>Uploaded By</th>
                <th style={styles.erpTh}>Uploaded Date</th>
                <th style={styles.erpTh}></th>
              </tr>
            </thead>
            <tbody>
              {filteredManuals.length > 0 ? (
                filteredManuals.map((m) => (
                  <tr key={m.id}>
                    <td style={styles.erpTd}>{m.module_name || "-"}</td>
                    <td style={styles.erpTd}>{m.process_name || "-"}</td>
                    <td style={styles.erpTd}>{m.sub_process_name || "-"}</td>
                    <td style={styles.erpTd}>
                      <span style={{ marginRight: 6 }}>{getFileIcon(m.file_name)}</span>
                      {m.file_name}
                    </td>
                    <td style={styles.erpTd}>{m.uploaded_by || "-"}</td>
                    <td style={styles.erpTd}>{formatDate(m.uploaded_at)}</td>
                    <td style={styles.erpTd}>
                      <div style={{ display: "flex", gap: 6 }}>
                        {/* <a href={m.drive_link || "#"}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-outline-dark btn-sm"
                          style={{ fontSize: "11px", padding: "2px 10px" }}
                        >
                          View
                        </a> */}
                        <button
                          title="Delete"
                          style={{
                            background: "#fff", border: "1px solid #f5c2c7", borderRadius: "4px",
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            padding: "4px 8px", cursor: "pointer"
                          }}
                          onClick={() => setDeleteTarget(m)}
                        >
                          <svg width="14" height="14" viewBox="0 0 16 16" fill="#dc3545" xmlns="http://www.w3.org/2000/svg">
                            <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6Z" />
                            <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1ZM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118ZM2.5 3V2h11v1h-11Z" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                !loadingList && (
                  <tr>
                    <td colSpan={7} className="text-center text-muted p-3">No user manuals uploaded yet</td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>

        {/* ===================== UPLOAD MODAL ===================== */}
        {showModal && (
          <div style={styles.modalBackdrop}>
            <div style={styles.modal}>
              <div className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h4 style={{ margin: 0 }}>Upload User Manual</h4>
                  <button className="btn btn-danger btn-sm" onClick={() => { setShowModal(false); setAlert(null); }}>X</button>
                </div>

                {alert && (
                  <div style={styles.alertBox(alert.type)}>
                    <span>{alert.message}</span>
                    <button onClick={() => setAlert(null)} style={{ background: "none", border: "none", fontWeight: 700, cursor: "pointer", color: "inherit", fontSize: "14px" }}>&times;</button>
                  </div>
                )}

                <div className="mb-3">
                  <label style={styles.compactLabel}>Module <span style={{ color: "#dc3545" }}>*</span></label>
                  <Select
                    styles={customSelectStyles}
                    options={moduleOptions}
                    value={uploadForm.moduleName ? { value: uploadForm.moduleName, label: uploadForm.moduleName } : null}
                    placeholder="Select"
                    isClearable
                    onChange={(v) => handleUploadFieldChange("moduleName", v ? v.value : "")}
                  />
                </div>

                <div className="mb-3">
  <label style={styles.compactLabel}>Process <span style={{ color: "#dc3545" }}>*</span></label>                  
                 <Select
                    styles={customSelectStyles}
                    options={getProcessOptions(uploadForm.moduleName)}
                    value={uploadForm.process ? { value: uploadForm.process, label: uploadForm.process } : null}
                    placeholder="Select"
                    isClearable
                    onChange={(v) => handleUploadFieldChange("process", v ? v.value : "")}
                  />
                </div>

                <div className="mb-3">
                  <label style={styles.compactLabel}>Sub Process</label>
                  <Select
                    styles={customSelectStyles}
                    options={getSubProcessOptions(uploadForm.moduleName, uploadForm.process)}
                    value={uploadForm.subProcess ? { value: uploadForm.subProcess, label: uploadForm.subProcess } : null}
                    placeholder="Select"
                    isClearable
                    onChange={(v) => handleUploadFieldChange("subProcess", v ? v.value : "")}
                  />
                </div>

                <div className="mb-4">
                  <label style={styles.compactLabel}>File <span style={{ color: "#dc3545" }}>*</span></label>
                  <label
                    htmlFor="userManualFileInput"
                    style={{
                      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                      border: "2px dashed #cbd5e1", borderRadius: 8, padding: "18px 12px", cursor: "pointer", background: "#f8fafc"
                    }}
                  >
                    <span style={{ fontSize: 22 }}>{uploadForm.file ? "✅" : "📁"}</span>
                    <span style={{ fontSize: 13, color: "#475569", fontWeight: 500, marginTop: 4 }}>
                      {uploadForm.file ? uploadForm.file.name : "Click to choose a file"}
                    </span>
                    <span style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>PDF, Word, Excel, etc.</span>
                    <input
                      id="userManualFileInput"
                      type="file"
                      style={{ display: "none" }}
                      onChange={(e) => handleUploadFieldChange("file", e.target.files[0] || null)}
                    />
                  </label>
                </div>

                <div className="d-flex justify-content-end">
                  <button
                    style={styles.createBtn}
                    onClick={handleUpload}
                    disabled={uploading || !uploadForm.moduleName || !uploadForm.process || !uploadForm.file}
                  >
                    {uploading ? "Uploading..." : "Upload"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===================== DELETE CONFIRM ===================== */}
        {deleteTarget && (
          <div style={styles.modalBackdrop} onClick={() => setDeleteTarget(null)}>
            <div style={{ ...styles.modal, width: "380px", maxWidth: "90vw" }} onClick={(e) => e.stopPropagation()}>
              <div className="p-4">
                <h5 style={{ margin: "0 0 10px 0" }}>Delete User Manual</h5>
                <p style={{ fontSize: "13px", color: "#334155", margin: "0 0 20px 0" }}>
                  Delete "{deleteTarget.file_name}"? This action cannot be undone.
                </p>
                <div className="d-flex justify-content-end" style={{ gap: 8 }}>
                  <button className="btn btn-outline-secondary btn-sm" onClick={() => setDeleteTarget(null)}>Cancel</button>
                  <button className="btn btn-danger btn-sm" onClick={confirmDelete}>Delete</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default UserManual;